/* Implements Warm Academic theme styling for practice quiz session */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BackendQuestion, QuestionDifficulty } from '../types';
import { 
  Brain, CheckCircle, ArrowRight, Zap, 
  BookOpen, Loader2, Archive, Trophy, Sparkles,
  MapPin, X, ChevronDown, AlertTriangle, ChevronRight,
  ListOrdered, RotateCcw, ShieldCheck, TrendingDown, Database,
  Globe, Info, Lightbulb, Layers, Activity, Bot, Check,
  Target, GraduationCap, Medal, Flame, Calendar, BookOpenCheck,
  Award, HelpCircle, History, Clock, Keyboard, Star, Volume2, VolumeX, Lock
} from 'lucide-react';
import { StorageService } from '../services/storageService';
import { MissedQuestionsService } from '../services/missedQuestionsService';
import { GamificationService } from '../services/gamificationService';
import * as GeminiService from '../services/geminiService';
import { AdIntervalBreak } from './AdIntervalBreak';
import { SponsoredService } from '../services/sponsoredService';
import { useAuth } from './AuthBridge';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { PageLoader } from './PageLoader';
import { AdUnit } from './AdUnit';
import confetti from 'canvas-confetti';
import { checkAndTriggerFirstQuizOfDayToast } from './ToastNotification';
import { AdvanceSuggestionPanel } from './AdvanceSuggestionPanel';
import { DifficultyRatingWidget } from './DifficultyRatingWidget';
import { DifficultyRatingBadge } from './DifficultyRatingBadge';
import { GuestTrialService } from '../services/guestTrialService';
import { GuestLimitModal } from './GuestLimitModal';
import { AiFeatureAuthModal } from './AiFeatureAuthModal';
import { US_STATES, CNA_DOMAINS, getPracticeQuestions, MOCK_POOL } from '../data/mockData';

interface PracticeQuizSessionProps {
  defaultTopic?: string;
}

// Custom meta resolver for domains to make them look incredibly engaging & Quizlet-like
const getDomainMeta = (domainName: string) => {
  const low = domainName.toLowerCase();
  
  if (low.includes('missed') || low.includes('vault') || low.includes('mistake')) {
    return {
      desc: 'Remediate historical errors. Clear these items by answering them correctly twice in a row.',
      icon: Archive,
      color: 'text-rose-500 dark:text-rose-400',
      bg: 'bg-rose-50 dark:bg-rose-950/20 border-rose-100 dark:border-rose-900/30',
      tag: 'Adaptive Recovery',
      badgeColor: 'bg-rose-500 text-white'
    };
  }

  if (low.includes('mixed') || low.includes('all')) {
    return {
      desc: 'An adaptive marathon drill combining random questions across all certified clinical medical domains.',
      icon: Zap,
      color: 'text-indigo-600 dark:text-indigo-400',
      bg: 'bg-indigo-50 dark:bg-indigo-950/20 border-indigo-100 dark:border-indigo-900/30',
      tag: 'Practice Marathon',
      badgeColor: 'bg-indigo-600 text-white'
    };
  }

  if (low.includes('basic nursing') || low.includes('clinical')) {
    return {
      desc: 'Master basic physical care metrics, vital signs, physical measurements, and clinical handoffs.',
      icon: Activity,
      color: 'text-indigo-500 dark:text-indigo-400',
      bg: 'bg-indigo-500/10 border-indigo-500/20',
      tag: 'Clinical Core',
      badgeColor: 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300'
    };
  }
  if (low.includes('personal care') || low.includes('grooming') || low.includes('hygiene')) {
    return {
      desc: 'Learn high-contrast ADL routines, hygiene safety barriers, grooming support, and physical aid skills.',
      icon: Layers,
      color: 'text-amber-500 dark:text-amber-400',
      bg: 'bg-amber-500/10 border-amber-500/20',
      tag: 'ADLs Skills',
      badgeColor: 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
    };
  }
  if (low.includes('mental health') || low.includes('social') || low.includes('cognit')) {
    return {
      desc: 'Understand dementia care protocols, behavioral de-escalation, patient dignity support, and empathy structures.',
      icon: Brain,
      color: 'text-purple-500 dark:text-purple-405',
      bg: 'bg-purple-500/10 border-purple-500/20',
      tag: 'Psychosocial Support',
      badgeColor: 'bg-purple-500/20 text-purple-700 dark:text-purple-300'
    };
  }
  if (low.includes('rights') || low.includes('legal') || low.includes('ethical') || low.includes('resident')) {
    return {
      desc: 'Protect patient rights, master state confidentiality, dynamic abuse tracking, and privacy standards.',
      icon: ShieldCheck,
      color: 'text-emerald-500 dark:text-emerald-400',
      bg: 'bg-emerald-500/10 border-emerald-500/20',
      tag: 'Dignity & Autonomy',
      badgeColor: 'bg-emerald-500/20 text-emerald-750 dark:text-emerald-300'
    };
  }
  if (low.includes('communication') || low.includes('interaction')) {
    return {
      desc: 'Strengthen medical terminology handoffs, documentation, reporting barriers, and conflict solutions.',
      icon: Globe,
      color: 'text-sky-500 dark:text-sky-400',
      bg: 'bg-sky-500/10 border-sky-500/20',
      tag: 'Clinical Handoffs',
      badgeColor: 'bg-sky-500/20 text-sky-750 dark:text-sky-300'
    };
  }
  if (low.includes('safety') || low.includes('emergency') || low.includes('infection')) {
    return {
      desc: 'Acknowledge hand sanitation rules, sterile boundaries, choking support, and fire evacuation plans.',
      icon: AlertTriangle,
      color: 'text-rose-500 dark:text-rose-400',
      bg: 'bg-rose-500/10 border-rose-500/20',
      tag: 'Clinical Biosecurity',
      badgeColor: 'bg-rose-500/20 text-rose-750 dark:text-rose-300'
    };
  }
  return {
    desc: 'Practice sets for certified learning. Access targeted clinical rationales immediately.',
    icon: GraduationCap,
    color: 'text-teal-600 dark:text-teal-400',
    bg: 'bg-teal-500/10 border-teal-500/20',
    tag: 'Board Practice',
    badgeColor: 'bg-teal-500/20 text-teal-750 dark:text-teal-300'
  };
};

interface QuizCircularProgressRingProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  accentColorClass?: string;
  trackColorClass?: string;
  label?: string;
  sublabel?: React.ReactNode;
}

const QuizCircularProgressRing: React.FC<QuizCircularProgressRingProps> = ({
  progress,
  size = 36,
  strokeWidth = 3.5,
  accentColorClass = 'text-indigo-600 dark:text-indigo-400',
  trackColorClass = 'text-slate-200 dark:text-slate-700',
  label,
  sublabel,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.min(100, Math.max(0, progress));
  const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;

  return (
    <div className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2.5 py-1 sm:py-1.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm shrink-0 min-w-0 max-w-full overflow-hidden">
      <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className={`fill-none stroke-current ${trackColorClass}`}
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className={`fill-none stroke-current ${accentColorClass} transition-all duration-500 ease-out`}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute text-[7.5px] sm:text-[9px] font-black tracking-tighter tabular-nums text-slate-800 dark:text-slate-200">
          {Math.round(clampedProgress)}%
        </span>
      </div>
      {(label || sublabel) && (
        <div className="flex flex-col text-left pr-0.5 min-w-0 shrink-0">
          {label && (
            <span className="text-[9.5px] sm:text-xs font-black tracking-tight tabular-nums text-slate-900 dark:text-slate-100 whitespace-nowrap">
              {label}
            </span>
          )}
          {sublabel && (
            <span className="text-[7px] sm:text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider leading-tight mt-0.5 whitespace-nowrap">
              {sublabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const processAndDeduplicateCategories = (rawCats: { category: string; count: number }[]) => {
  const map = new Map<string, { category: string; count: number }>();
  for (const item of rawCats) {
    if (!item || !item.category) continue;
    const trimmed = item.category.trim();
    if (!trimmed) continue;
    
    const normKey = trimmed.toLowerCase();
    const existingKey = Array.from(map.keys()).find(k => k.toLowerCase() === normKey);
    
    if (existingKey) {
      const existing = map.get(existingKey)!;
      existing.count += item.count || 0;
    } else {
      map.set(trimmed, { category: trimmed, count: item.count || 0 });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

export const PracticeQuizSession: React.FC<PracticeQuizSessionProps> = ({ defaultTopic = 'Mixed' }) => {
  const { user, activeCert, updateUser } = useAuth();
  const tokens = useThemeTokens();
  const activeCategories = useMemo(() => {
    const raw = activeCert?.categories?.length ? activeCert.categories : CNA_DOMAINS;
    const set = new Set<string>();
    const result: string[] = [];
    raw.forEach(c => {
      if (!c) return;
      const t = c.trim();
      const lower = t.toLowerCase();
      if (t && !set.has(lower)) {
        set.add(lower);
        result.push(t);
      }
    });
    return result;
  }, [activeCert]);
  const [config, setConfig] = useState(SponsoredService.getConfig());

  useEffect(() => {
    const handleUpdate = () => {
      setConfig(SponsoredService.getConfig());
    };
    window.addEventListener('sponsored-config-updated', handleUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);
  
  // Quiz view states
  const [isSetup, setIsSetup] = useState(true);
  const [topic, setTopic] = useState<string>(defaultTopic);
  const [difficulty, setDifficulty] = useState<QuestionDifficulty>('Medium');
  const [questionCount, setQuestionCount] = useState<number>(user?.preferences?.defaultQuestionCount || 10);
  const [isLoading, setIsLoading] = useState(false);
  const [missedCount, setMissedCount] = useState(0);
  const [vaultQuestions, setVaultQuestions] = useState<BackendQuestion[]>([]);
  const [questions, setQuestions] = useState<BackendQuestion[]>([]);
  
  // Game states
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [systemError, setSystemError] = useState<string | null>(null);
  const [stateCode, setStateCode] = useState<string>(user?.state || 'Global');
  const [currentSequence, setCurrentSequence] = useState<string[]>([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isSetup && !isCompleted) {
      const timer = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isSetup, isCompleted]);
  
  // Ad and AI states
  const [showAdBreak, setShowAdBreak] = useState(false);
  const [lastAdIndex, setLastAdIndex] = useState(-1);
  const [adRefreshKey, setAdRefreshKey] = useState(0);
  const [aiRationale, setAiRationale] = useState<{whyIncorrect: string; correctRationale?: string} | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [showAiAuthModal, setShowAiAuthModal] = useState(false);

  // Audio Speech Synthesis (TTS) State and Helper Hooks
  const [isSpeaking, setIsSpeaking] = useState(false);
  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Guest trial exploration tracking
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);
  const isGuest = !user;
  const guestLimit = config.guestQuestionLimit || 50;
  const [guestState, setGuestState] = useState(() => GuestTrialService.getState());

  useEffect(() => {
    const handleGuestUpdate = (e: any) => {
      if (e?.detail) setGuestState(e.detail);
      else setGuestState(GuestTrialService.getState());
    };
    window.addEventListener('guest-trial-updated', handleGuestUpdate);
    return () => window.removeEventListener('guest-trial-updated', handleGuestUpdate);
  }, []);

  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      activeUtteranceRef.current = null;
    }
    setIsSpeaking(false);
  }, [currentIndex, isCompleted]);

  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
      }
    };
  }, []);

  const handleSpeak = (textToSpeak: string) => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
        setIsSpeaking(false);
        return;
      }
      window.speechSynthesis.cancel();
      const cleanText = textToSpeak
        .replace(/<[^>]*>/g, ' ')
        .replace(/[*#`_~]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const utterance = new SpeechSynthesisUtterance(cleanText);
      activeUtteranceRef.current = utterance;
      utterance.onend = () => {
        activeUtteranceRef.current = null;
        setIsSpeaking(false);
      };
      utterance.onerror = () => {
        activeUtteranceRef.current = null;
        setIsSpeaking(false);
      };
      setIsSpeaking(true);
      window.speechSynthesis.speak(utterance);
    }
  };

  const activeCertId = activeCert?.id || user?.activeCertId || 'cna';

  // Dynamic category state loaded from backend
  const [dynamicCategories, setDynamicCategories] = useState<{ category: string; count: number }[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      setIsCategoriesLoading(true);
      try {
        const res = await StorageService.getQuestionCategories(activeCertId, stateCode);
        if (isMounted && res && Array.isArray(res.categories) && res.categories.length > 0) {
          setDynamicCategories(processAndDeduplicateCategories(res.categories));
          setIsCategoriesLoading(false);
          return;
        }
      } catch (err) {
        console.warn("Failed to retrieve dynamic categories from backend:", err);
      }

      // Fallback: derive categories with >0 questions from storage or MOCK_POOL
      try {
        let pool: BackendQuestion[] = [];
        const qRes = await StorageService.getQuestions(activeCertId, { limit: 1000, state: stateCode }).catch(() => null);
        if (qRes && qRes.items && qRes.items.length > 0) {
          pool = qRes.items;
        } else if (activeCertId === 'cna') {
          pool = MOCK_POOL;
        }

        const counts: Record<string, number> = {};
        pool.forEach(q => {
          if (q.category) {
            const trimmed = q.category.trim();
            counts[trimmed] = (counts[trimmed] || 0) + 1;
          }
        });

        const derived = processAndDeduplicateCategories(
          Object.entries(counts)
            .filter(([_, c]) => c > 0)
            .map(([cat, count]) => ({ category: cat, count }))
        );

        if (isMounted) {
          setDynamicCategories(derived);
        }
      } catch (e) {
        console.error("Error computing fallback categories:", e);
      } finally {
        if (isMounted) setIsCategoriesLoading(false);
      }
    };

    fetchCategories();
    return () => { isMounted = false; };
  }, [activeCertId, stateCode]);

  // Load mistake vault statistics
  useEffect(() => {
    if (isSetup) {
      const count = MissedQuestionsService.getCount();
      setMissedCount(count);
      setStateCode(user?.state || 'Global');
      
      if (count > 0) {
          MissedQuestionsService.getQuestions(activeCertId).then(setVaultQuestions).catch(err => {
              console.warn("Registry sync failed for vault items.");
          });
      } else {
          setVaultQuestions([]);
      }
    }
  }, [isSetup, user?.state, activeCertId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('onboarding') === 'true' && isSetup && !isLoading && questions.length === 0 && !systemError) {
      setQuestionCount(5);
      startQuiz('Mixed');
    }
  }, [isSetup, isLoading, questions.length, systemError]);

  const missedBreakdown = useMemo(() => {
      const counts: Record<string, number> = {};
      if (!vaultQuestions.length) return [];
      vaultQuestions.forEach(q => {
          if (q.category) {
            counts[q.category] = (counts[q.category] || 0) + 1;
          }
      });
      return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [vaultQuestions]);

  const startQuiz = async (forcedTopic?: string) => {
    const activeTopic = forcedTopic || topic;
    setIsLoading(true);
    setSystemError(null);
    try {
        if (stateCode !== user?.state) {
            await updateUser({ state: stateCode, stateManuallySet: true });
        }

        let pool: BackendQuestion[] = [];
        
        if (activeTopic === 'Missed Questions') {
            pool = await MissedQuestionsService.getQuestions(activeCertId);
            if (pool.length === 0) {
                const count = MissedQuestionsService.getCount();
                if (count > 0) {
                    setSystemError(`Found ${count} items in vault, but data is unreachable. Verify registry connection.`);
                } else {
                    setSystemError("The Mistake Vault is currently synchronized. No remediation required.");
                }
                setIsLoading(false);
                return;
            }
        } else {
            try {
                const res = await StorageService.getQuestions(activeCertId, { 
                    limit: 1000, 
                    category: ['Mixed', 'Mixed Domain', 'Mixed Domain Exam', 'Mixed Practice'].includes(activeTopic) ? undefined : activeTopic, 
                    state: stateCode 
                });
                if (res && res.items && res.items.length > 0) {
                    pool = res.items;
                }
            } catch (err) {
                console.warn("Registry partition unreachable.");
            }
            
            if (pool.length === 0 && activeCertId === 'cna') {
                pool = getPracticeQuestions(activeCertId, activeTopic, 100, stateCode, difficulty);
            }
        }
        
        if (pool.length === 0) {
            setSystemError(`The clinical registry for ${activeCert?.shortName || activeCertId} is currently empty.`);
            setIsLoading(false); 
            return;
        }
        
        const shuffled = [...pool].sort(() => 0.5 - Math.random());
        const finalSelection = shuffled.slice(0, Math.min(questionCount, shuffled.length));
        
        setQuestions(finalSelection);
        setTopic(activeTopic);
        setCurrentIndex(0); 
        setScore(0); 
        setSelectedOptionId(null); 
        setIsAnswered(false); 
        setIsCorrect(false);
        setIsCompleted(false);
        setLastAdIndex(-1);
        setShowAdBreak(false);
        setCurrentSequence([]);
        setElapsedSeconds(0);

        // Small simulation delay for premium feel
        await new Promise(resolve => setTimeout(resolve, 800));

        setIsSetup(false);
        if (window.trackEvent) window.trackEvent('quiz_launched', 'quiz', activeTopic);
    } catch (e) {
        setSystemError("Logic sequencer interruption.");
    } finally {
        setIsLoading(false);
    }
  };

  const handleOptionSelect = (optionId: string, isCorrectOption: boolean) => {
    if (isAnswered) return;
    const q = questions[currentIndex];

    if (q.type === 'ordering') {
        if (currentSequence.includes(optionId)) return;
        const next = [...currentSequence, optionId];
        setCurrentSequence(next);
        if (next.length === q.options.length) {
            const correctSequence = q.options.map((o, oIdx) => o._id || oIdx.toString()).join(',');
            const userSequence = next.join(',');
            const isMatch = String(correctSequence) === String(userSequence);
            finalizeItem(isMatch);
        }
    } else {
        setSelectedOptionId(optionId);
        
        const optionObj = q.options?.find((o, idx) => o._id === optionId || idx.toString() === optionId);
        const resolvedCorrect = optionObj?.isCorrect === true || 
                               String(optionObj?.isCorrect).toLowerCase() === 'true' || 
                               Number(optionObj?.isCorrect) === 1;
                               
        finalizeItem(resolvedCorrect, optionId);
    }
  };

  const finalizeItem = async (correct: boolean, optionId?: string) => {
    setIsAnswered(true);
    setIsCorrect(correct);
    const q = questions[currentIndex];

    // Check & track guest trial mode limits
    if (isGuest && config.isGuestTrialEnabled !== false && q) {
      const { allowed, totalUsed, remaining } = GuestTrialService.trackQuestionAnswered(q._id, guestLimit);
      if (!allowed) {
        setShowGuestLimitModal(true);
        StorageService.trackUserEvent({
          action: 'guest_limit_reached',
          category: 'guest_trial',
          label: `Limit reached (${totalUsed}/${guestLimit}) on question`,
          value: totalUsed,
          metadata: {
            limit: guestLimit,
            totalExplored: totalUsed,
            remaining,
            questionId: q._id
          }
        });
      }
    }
    
    // Log individual question answer telemetry
    if (q) {
        StorageService.trackUserEvent({
            action: 'question_answer',
            category: 'practice_quiz',
            label: q.text || q.question || 'Practice Question',
            value: correct ? 1 : 0,
            metadata: {
                questionId: q._id,
                wasCorrect: correct,
                optionId: optionId,
                topic: topic || 'General'
            }
        });
    }

    if (correct) {
      setScore(prev => prev + 1);
      if (topic === 'Missed Questions') MissedQuestionsService.recordSuccess(q._id);
      GamificationService.addPoints(15); // Dynamic XP increase
    } else {
      MissedQuestionsService.addQuestion(q._id);
    }
  };

  const triggerAiRationale = async () => {
    // Strictly guard AI feature for authenticated users only to protect AI models and save costs
    if (isGuest || !user) {
      setShowAiAuthModal(true);
      return;
    }

    const q = questions[currentIndex];
    if (!config.enableAIRationales || q.type === 'ordering' || aiRationale || isAiLoading) return;

    setIsAiLoading(true);
    try {
      const correctAnswer = q.options.find(o => 
          o.isCorrect === true || String(o.isCorrect).toLowerCase() === 'true' || Number(o.isCorrect) === 1
      );
      const result = await GeminiService.getDeepRationale(
        q.text, 
        q.options, 
        correctAnswer?._id || '0', 
        activeCert?.name || 'Certification',
        selectedOptionId || ''
      );
      
      if (result && result.distractorAnalyses) {
        const analysis = result.distractorAnalyses.find((a: any) => a.optionId === selectedOptionId);
        setAiRationale({
          whyIncorrect: analysis?.whyIncorrect || result.distractorAnalyses[0]?.whyIncorrect || "This option does not meet clinical safety standards.",
          correctRationale: result.correctRationale
        });
      }
    } catch (err) {
      console.error("AI Rationale Retrieval Failed:", err);
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleExplainMismatchClick = () => {
    if (isGuest || !user) {
      setShowAiAuthModal(true);
      StorageService.trackUserEvent({
        action: 'explain_mismatch_guest_clicked',
        category: 'conversion',
        label: 'Guest clicked Explain My Mismatch',
        metadata: {
          questionId: questions[currentIndex]?._id,
          category: questions[currentIndex]?.category,
          topic
        }
      });
      return;
    }
    triggerAiRationale();
  };

  const handleNext = () => {
    // Check if guest user has exceeded exploration limit
    if (isGuest && config.isGuestTrialEnabled !== false && GuestTrialService.hasReachedLimit(guestLimit)) {
      setShowGuestLimitModal(true);
      return;
    }

    const nextIndex = currentIndex + 1;
    const interval = config.intervals.practiceQuiz || 7;
    const rotationThreshold = config.intervals.adRotationThreshold || 10;

    if (nextIndex > 0 && nextIndex % rotationThreshold === 0) {
        setAdRefreshKey(prev => prev + 1);
    }

    if (user?.planTier === 'free' && config.isActive && config.intermissionEnabled && nextIndex > 0 && nextIndex % interval === 0 && lastAdIndex !== nextIndex && nextIndex < questions.length) {
        setShowAdBreak(true);
        setLastAdIndex(nextIndex);
        return;
    }

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(nextIndex);
      setSelectedOptionId(null);
      setIsAnswered(false);
      setIsCorrect(false);
      setAiRationale(null);
      setCurrentSequence([]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsCompleted(true);
      const finalPercentReal = Math.round((score / Math.max(1, questions.length)) * 100);
      
      const isOnboardingQuiz = new URLSearchParams(window.location.search).get('onboarding') === 'true';
      if (isOnboardingQuiz) {
         const currentHistory = user?.history || [];
         const newEntry = {
             id: 'onboarding_' + Date.now(),
             certId: activeCertId,
             score: finalPercentReal,
             passed: finalPercentReal >= 75,
             date: new Date().toISOString(),
             mode: 'practice_quiz',
             category: 'Baseline Drill'
         };
         const currentStats = user?.stats || {
             quizzesTaken: 0,
             averageScore: 0,
             studyHours: 0,
             topicsMastered: 0,
             points: 0,
             level: 1,
             badges: []
         };
         const updatedStats = {
             ...currentStats,
             quizzesTaken: (currentStats.quizzesTaken || 0) + 1,
             points: (currentStats.points || 0) + 100,
             averageScore: Math.round(((currentStats.averageScore || 0) * (currentStats.quizzesTaken || 0) + finalPercentReal) / ((currentStats.quizzesTaken || 0) + 1))
         };
         updateUser({
             history: [newEntry, ...currentHistory],
             stats: updatedStats
         }).catch(err => console.error("Onboarding quiz state saving failure", err));
      }

      // Log whole practice quiz completed attempt to telemetry
      StorageService.trackUserEvent({
          action: 'practice_attempt',
          category: 'practice_quiz',
          label: topic || 'General',
          value: finalPercentReal,
          metadata: {
              totalQuestions: questions.length,
              score: score,
              passed: finalPercentReal >= 75
          }
      });

      if (window.trackEvent) window.trackEvent('quiz_finished', 'quiz', topic, finalPercentReal);

      // Trigger first quiz of the day toast notification
      checkAndTriggerFirstQuizOfDayToast();

      // Confetti celebration animation for score above 85%
      if (finalPercentReal >= 85) {
        try {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
          setTimeout(() => {
            confetti({
              particleCount: 60,
              angle: 60,
              spread: 55,
              origin: { x: 0, y: 0.7 }
            });
            confetti({
              particleCount: 60,
              angle: 120,
              spread: 55,
              origin: { x: 1, y: 0.7 }
            });
          }, 250);
        } catch (err) {
          console.error("Confetti launch error:", err);
        }
      }
    }
  };

  const handleBackToRegistry = () => {
      setIsCompleted(false);
      setIsSetup(true);
      setQuestions([]);
      setCurrentIndex(0);
      setScore(0);
      setSelectedOptionId(null);
      setIsAnswered(false);
      setIsCorrect(false);
      setSystemError(null);
  };

  // Safe Keyboard Shortcuts similar to standard Quizlet features
  useEffect(() => {
    const handleKeyShortcut = (e: KeyboardEvent) => {
        if (isSetup || isCompleted || showAdBreak) return;
        
        // Block shortcuts inside inputs
        const targetTag = e.target && (e.target as HTMLElement).tagName.toLowerCase();
        if (targetTag === 'input' || targetTag === 'textarea' || targetTag === 'select') return;

        const currentQ = questions[currentIndex];
        if (!currentQ) return;

        if (isAnswered) {
            if (e.code === 'Space' || e.code === 'ArrowRight' || e.code === 'Enter') {
                e.preventDefault();
                handleNext();
            }
        } else {
            // Options picker
            if (e.code === 'KeyA' || e.code === 'Digit1') { e.preventDefault(); handleOptionSelect('0', !!currentQ.options[0]?.isCorrect); }
            else if (e.code === 'KeyB' || e.code === 'Digit2') { e.preventDefault(); handleOptionSelect('1', !!currentQ.options[1]?.isCorrect); }
            else if (e.code === 'KeyC' || e.code === 'Digit3') { e.preventDefault(); handleOptionSelect('2', !!currentQ.options[2]?.isCorrect); }
            else if (e.code === 'KeyD' || e.code === 'Digit4') { e.preventDefault(); handleOptionSelect('3', !!currentQ.options[3]?.isCorrect); }
        }
    };

    window.addEventListener('keydown', handleKeyShortcut);
    return () => window.removeEventListener('keydown', handleKeyShortcut);
  }, [isSetup, isCompleted, showAdBreak, isAnswered, currentIndex, questions]);

  if (isCompleted) {
    const finalPercent = Math.round((score / Math.max(1, questions.length)) * 100);
    const earnsLevelBadge = finalPercent >= 80;
    
    return (
        <div className="max-w-4xl mx-auto py-6 md:py-12 animate-fade-in text-slate-900 dark:text-slate-100 px-3 md:px-4">
            <div className={`rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-16 text-white text-center shadow-2xl relative overflow-hidden group ${topic === 'Missed Questions' ? 'bg-gradient-to-br from-rose-950 via-slate-900 to-rose-950' : tokens.heroBannerBg} border border-white/10`}>
                <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 blur-3xl rounded-full pointer-events-none"></div>
                <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-indigo-500/10 blur-3xl rounded-full pointer-events-none"></div>

                <div className="relative z-10 space-y-8 md:space-y-10">
                    {/* Achievement Icon */}
                    <div className="relative">
                        <div className={`w-20 h-20 md:w-28 md:h-28 rounded-[2rem] flex items-center justify-center mx-auto shadow-2xl transition-transform duration-700 group-hover:rotate-6 group-hover:scale-110 border-b-4 ${topic === 'Missed Questions' ? 'bg-rose-500 border-rose-700' : tokens.primaryBtn}`}>
                            <Trophy size={44} className="text-white animate-bounce-slow" />
                        </div>
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-1 flex gap-1">
                            <Sparkles className="text-amber-400 animate-pulse" size={16} />
                        </div>
                    </div>

                    <div>
                        <span className="px-3 py-1 bg-white/10 border border-white/15 rounded-full font-black text-[9px] tracking-widest uppercase">
                            {topic === 'Missed Questions' ? 'Vulnerability Cleared' : 'Module Logic Completed'}
                        </span>
                        <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-none mt-3">
                            {topic === 'Missed Questions' ? 'Vault Remediation' : 'Practice Drill Score'}
                        </h2>
                        <p className="text-slate-400 text-xs font-bold max-w-md mx-auto mt-2">
                            Excellent work! Each practice block builds cognitive memory pathways required for board speed runs.
                        </p>
                    </div>

                    {/* Results Grid Stats */}
                    <div className="grid grid-cols-3 gap-3 md:gap-6 bg-white/5 border border-white/10 p-5 md:p-8 rounded-3xl max-w-2xl mx-auto backdrop-blur-sm text-center">
                        <div className="space-y-1">
                            <p className="text-3xl md:text-5xl font-black text-white tabular-nums">{score}</p>
                            <p className="text-[8px] md:text-[9.5px] font-black text-slate-450 tracking-widest uppercase">Correct</p>
                        </div>
                        <div className="border-x border-white/10 space-y-1">
                            <p className="text-3xl md:text-5xl font-black text-emerald-400 tabular-nums">{finalPercent}%</p>
                            <p className="text-[8px] md:text-[9.5px] font-black text-slate-450 tracking-widest uppercase">Accuracy</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-3xl md:text-5xl font-black text-amber-400 tabular-nums">+{score * 15} XP</p>
                            <p className="text-[8px] md:text-[9.5px] font-black text-slate-450 tracking-widest uppercase">Earned</p>
                        </div>
                    </div>

                    {/* Additional remediation updates */}
                    {topic === 'Missed Questions' && (
                        <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl max-w-md mx-auto flex items-center justify-center gap-2 text-xs font-bold text-emerald-300">
                            <ShieldCheck size={16} /> Clearance sequence registered in your adaptive cache.
                        </div>
                    )}

                    <div className="pt-2">
                        <button 
                            onClick={handleBackToRegistry} 
                            className="px-10 py-4 md:py-5 bg-white text-slate-900 hover:bg-slate-50 transition-all active:scale-95 rounded-2xl font-black text-xs tracking-widest shadow-2xl flex items-center justify-center gap-2 mx-auto border-b-4 border-slate-300 hover:border-slate-400"
                        >
                            <RotateCcw size={16} /> Return to Quiz sets
                        </button>
                    </div>
                </div>
            </div>

            {/* POST-QUIZ DIFFICULTY RATING WIDGET */}
            <DifficultyRatingWidget 
                itemId={`quiz-${topic}`}
                itemTitle={`${topic} Practice Quiz`}
                isExam={false}
                userId={user?.id || 'guest'}
            />

            <div className="mt-8 bg-white dark:bg-slate-950 p-6 md:p-8 rounded-[2.5rem] border border-slate-150 dark:border-slate-850 shadow-xl">
                <AdvanceSuggestionPanel 
                    source="quiz" 
                    score={finalPercent} 
                    moduleTitle={topic} 
                />
            </div>
        </div>
    );
  }

  if (isLoading) {
    return <PageLoader message="Loading Practice Quiz..." text="Compiling Practice Question Sets..." />;
  }

  if (showAdBreak) {
      return <AdIntervalBreak moduleName="Practice Drill" onComplete={() => setShowAdBreak(false)} />;
  }

  if (isSetup) {
    const isMissedMode = topic === 'Missed Questions';
    
    return (
      <div className="max-w-7xl mx-auto space-y-6 md:space-y-8 animate-fade-in pb-16 text-slate-900 dark:text-slate-100 px-3 md:px-6">
        
        {/* Global Error Banner */}
        {systemError && (
            <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/40 rounded-2xl flex items-center gap-3 animate-pop text-rose-700 dark:text-rose-300">
                <AlertTriangle size={20} className="shrink-0 text-rose-500" />
                <p className="text-xs font-bold flex-1">{systemError}</p>
                <button onClick={() => setSystemError(null)} className="p-1 hover:bg-rose-100 dark:hover:bg-rose-900/40 rounded-lg"><X size={14}/></button>
            </div>
        )}

        {/* TOP PANEL: HERO BOARD WORKSPACE & JURISDICTION SELECTION */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            
            {/* Dashboard left Hero */}
            <div className={`lg:col-span-8 p-6 md:p-10 rounded-[2.5rem] flex flex-col justify-between shadow-2xl relative overflow-hidden group border ${
              tokens.isWarmAcademic
                ? 'bg-[#0D6E6E] border-[#0B5C5C] text-white'
                : tokens.isClinical
                ? 'bg-gradient-to-br from-[#0B2942] via-[#0D3C5C] to-[#071F33] border-teal-500/20 text-white'
                : tokens.isHighContrast
                ? 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                : tokens.isMinimal
                ? 'bg-slate-900 text-white border-slate-800'
                : tokens.isVibrant
                ? 'bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-950 border-violet-800 text-white'
                : 'bg-gradient-to-br from-indigo-900 via-slate-900 to-slate-950 border-indigo-500/10 text-white'
            }`}>
                <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                    <Brain size={200} className="text-white transform rotate-12" />
                </div>
                
                <div className="relative z-10 space-y-4">
                    <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-white/10 rounded-full font-black text-[9px] tracking-widest uppercase text-teal-100">Diagnostic Drills Lab</span>
                        <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full font-black text-[9px] tracking-widest uppercase flex items-center gap-1">
                            <Sparkles size={8} /> Smart Style Active Cards
                        </span>
                    </div>
                    <h2 className={`text-3xl md:text-5xl font-black tracking-tight leading-tight max-w-xl ${tokens.isClinical ? 'font-serif' : ''}`}>
                        Optimize clinical mastery with targeted rapid drills
                    </h2>
                    <p className="text-xs md:text-sm text-slate-100 dark:text-slate-200 font-medium max-w-lg leading-relaxed">
                        Practice quizzes provide <span className={`font-black ${
                          tokens.isWarmAcademic ? 'text-amber-300' : tokens.isClinical ? 'text-teal-300' : tokens.isHighContrast ? 'text-amber-400' : tokens.isVibrant ? 'text-violet-300' : 'text-indigo-400'
                        }`}>instant rationales</span> and custom error insights upon answer selection. Choose a medical domain or run missed sets.
                    </p>
                </div>

                <div className="relative z-10 pt-8 mt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white/10 border border-white/20 p-3 rounded-2xl text-center">
                        <p className={`text-xl font-bold tabular-nums ${
                          tokens.isWarmAcademic ? 'text-amber-300' : tokens.isClinical ? 'text-teal-300' : tokens.isHighContrast ? 'text-amber-400' : tokens.isVibrant ? 'text-violet-300' : 'text-indigo-400'
                        }`}>+{user?.stats?.points || 0}</p>
                        <p className="text-[8px] font-black tracking-widest text-teal-100 uppercase mt-1">XP Points</p>
                    </div>
                    <div className="bg-white/10 border border-white/20 p-3 rounded-2xl text-center">
                        <p className="text-xl font-bold text-emerald-300 tabular-nums">Instant</p>
                        <p className="text-[8px] font-black tracking-widest text-teal-100 uppercase mt-1">Clinical Keys</p>
                    </div>
                    <div className="bg-white/10 border border-white/20 p-3 rounded-2xl text-center">
                        <p className="text-xl font-bold text-amber-300 tabular-nums">{(user?.history || []).length}</p>
                        <p className="text-[8px] font-black tracking-widest text-teal-100 uppercase mt-1">Drills Done</p>
                    </div>
                    <button onClick={() => startQuiz('Mixed')} className={`p-3 rounded-2xl text-center font-black text-xs tracking-widest transition-all flex flex-col items-center justify-center gap-1 active:scale-95 shadow-lg ${
                      tokens.isWarmAcademic
                        ? 'bg-[#E05638] text-white hover:bg-[#C8472B]'
                        : tokens.isClinical
                        ? 'bg-[#0F7D6C] text-white hover:bg-[#0B6356]'
                        : tokens.isHighContrast
                        ? 'bg-amber-400 text-black border border-black'
                        : tokens.isMinimal
                        ? 'bg-slate-800 text-white'
                        : tokens.isVibrant
                        ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}>
                        Mixed Quiz &rarr;
                    </button>
                </div>
            </div>

            {/* Dashboard right controls sidebar */}
            <div className="lg:col-span-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-805 p-6 rounded-[2.5rem] shadow-md flex flex-col justify-between space-y-6">
                
                <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800/60 pb-3">
                        <MapPin size={16} className={tokens.accentText} />
                        <h3 className="text-xs font-black tracking-widest uppercase text-slate-400">Drill Configurations</h3>
                    </div>

                    {/* US States Select box */}
                    <div className="space-y-2">
                        <label className="text-[9px] font-black text-slate-400 tracking-wider ml-1 flex items-center gap-1.5"><Globe size={11} /> Target State Jurisdiction</label>
                        <div className="relative group">
                            <select 
                                value={stateCode} 
                                onChange={e => setStateCode(e.target.value)} 
                                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 pl-4 pr-10 py-3 rounded-xl font-bold text-xs select-custom outline-none"
                            >
                                {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                            </select>
                            <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        </div>
                    </div>

                    {/* Question Count Presets */}
                    <div className="space-y-2 pt-2">
                        <label className="text-[9px] font-black text-slate-400 tracking-wider ml-1 flex items-center gap-1.5"><ListOrdered size={11} /> Logic Depth (Question Count)</label>
                        <div className="grid grid-cols-5 gap-1.5 bg-slate-50 dark:bg-slate-950 p-1 border border-slate-200 dark:border-slate-800 rounded-xl">
                            {[5, 10, 25, 50, 100].map(countVal => (
                                <button 
                                    key={countVal}
                                    onClick={() => setQuestionCount(countVal)}
                                    className={`py-2 text-[10px] font-black rounded-lg transition-all ${
                                      questionCount === countVal 
                                        ? (tokens.isWarmAcademic
                                            ? 'bg-[#E05638] text-white shadow-md'
                                            : tokens.isClinical
                                            ? 'bg-[#0F7D6C] text-white shadow-md'
                                            : tokens.isHighContrast
                                            ? 'bg-amber-400 text-black border border-black font-black'
                                            : tokens.isMinimal
                                            ? 'bg-slate-800 text-white'
                                            : tokens.isVibrant
                                            ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                                            : 'bg-indigo-600 text-white shadow-md')
                                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900'
                                    }`}
                                >
                                    {countVal}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={`p-4 rounded-2xl border flex gap-3 ${
                  tokens.isClinical
                    ? 'bg-teal-50 dark:bg-teal-950/40 border-teal-100/60 dark:border-teal-900/30 text-teal-950 dark:text-teal-300'
                    : tokens.isHighContrast
                    ? 'bg-amber-100 dark:bg-amber-950/40 border-2 border-black text-black'
                    : tokens.isMinimal
                    ? 'bg-slate-100 dark:bg-slate-800 border-slate-200 text-slate-900 dark:text-slate-200'
                    : tokens.isVibrant
                    ? 'bg-violet-50 dark:bg-violet-950/40 border-violet-100 dark:border-violet-900/30 text-violet-950 dark:text-violet-300'
                    : 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-100/40 dark:border-indigo-900/30 text-indigo-950 dark:text-indigo-300'
                }`}>
                    <Info size={16} className={`shrink-0 mt-0.5 ${tokens.accentText}`} />
                    <p className="text-[9.5px] font-bold leading-relaxed">
                        Select a clinically isolated topic or domain card from the grid below to target specific board protocols.
                    </p>
                </div>
            </div>
        </div>

        {/* SECTION 2: ADAPTIVE DECKS & CLINICAL PATHWAYS DOMAINS GRID */}
        <div className="space-y-6 pt-2">
            <div>
                <h3 className={`text-lg md:text-xl font-black text-slate-950 dark:text-white flex items-center gap-2 tracking-tight ${tokens.isClinical ? 'font-serif' : ''}`}>
                    <Layers size={20} className={tokens.accentText} /> Adaptive & Topic Study Decks
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-405 font-bold mt-1">
                    Select an active clinically integrated module below to clear mock questions, review remediation statistics, or reinforce logic.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* 1. MISTAKE VAULT - RECOVERY CARD STATS */}
                <div 
                    onClick={() => {
                        if (missedCount > 0) {
                            setTopic('Missed Questions');
                            startQuiz('Missed Questions');
                        }
                    }}
                    className={`relative bg-white dark:bg-slate-900 border ${missedCount > 0 ? 'border-rose-200 dark:border-rose-900/40 hover:border-rose-300 dark:hover:border-rose-800' : 'border-slate-200 dark:border-slate-805 opacity-60'} rounded-[2.2rem] p-6 shadow-md transition-all cursor-pointer transform hover:-translate-y-1 group flex flex-col justify-between h-64 overflow-hidden`}
                >
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="px-3 py-1 bg-rose-500/10 text-rose-600 dark:text-rose-450 rounded-full font-black text-[8px] tracking-widest uppercase border border-rose-500/20">
                                Adaptive Vault
                            </span>
                            <span className="text-[10px] font-black text-rose-550 flex items-center gap-1 gap-y-0 text-rose-500">
                                <Archive size={12}/> {missedCount} Gaps
                            </span>
                        </div>
                        <h4 className="text-lg font-black text-slate-950 dark:text-slate-50 tracking-tight leading-snug group-hover:text-rose-500 transition-colors">
                            The Mistake Vault
                        </h4>
                        <div className="pt-0.5">
                            <DifficultyRatingBadge itemId="quiz-Missed Questions" isExam={false} compact={true} />
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold leading-normal pt-0.5 line-clamp-2">
                            Clears automatically by forcing you to resolve missed questions twice correctly. Resolves clinical biases and structural test traps.
                        </p>
                    </div>

                    {/* Progress inside missed questions */}
                    <div className="space-y-2 mt-auto pt-4 border-t border-slate-100 dark:border-slate-850/40">
                        {missedCount > 0 ? (
                            <div className="space-y-1">
                                <div className="flex justify-between text-[8px] font-black text-rose-550 mt-1 uppercase tracking-widest">
                                    <span>Vulnerability Rate</span>
                                    <span>Gaps Ready</span>
                                </div>
                                <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${Math.min(100, (missedCount / 40) * 100)}%` }}></div>
                                </div>
                                <span className="text-[7.5px] font-bold text-rose-400 flex items-center gap-1 pt-1">
                                    Launch error remediation <ArrowRight size={8} />
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-500">
                                <CheckCircle size={14} /> Mistakes Vault fully cleared!
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. MIXED PRACTICE DECK */}
                <div 
                    onClick={() => {
                        setTopic('Mixed');
                        startQuiz('Mixed');
                    }}
                    className="relative bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-900/40 rounded-[2.2rem] p-6 shadow-md hover:shadow-xl transition-all hover:border-indigo-300 dark:hover:border-indigo-800 cursor-pointer transform hover:-translate-y-1 group flex flex-col justify-between h-64 overflow-hidden"
                >
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="px-3 py-1 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full font-black text-[8px] tracking-widest uppercase border border-indigo-500/20">
                                Global Review
                            </span>
                            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 flex items-center gap-1">
                                <Zap size={11}/> Infinite Mix
                            </span>
                        </div>
                        <h4 className="text-lg font-black text-slate-950 dark:text-slate-50 tracking-tight leading-snug group-hover:text-indigo-500 transition-colors">
                            Mixed Practice Marathon
                        </h4>
                        <div className="pt-0.5">
                            <DifficultyRatingBadge itemId="quiz-Mixed" isExam={false} compact={true} />
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold leading-normal pt-0.5 line-clamp-2">
                            A dynamic evaluation mixing randomized question types and clinical domains. An essential pre-exam checklist speed run to evaluate baseline competence.
                        </p>
                    </div>

                    <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-850/40">
                        <span className="text-[8.5px] font-black text-indigo-500 tracking-wider flex items-center justify-between">
                            <span>Adaptive Speed quiz</span>
                            <span className="group-hover:translate-x-1 transition-transform flex items-center gap-1">
                                Launch Mixed Practice <ChevronRight size={10} />
                            </span>
                        </span>
                    </div>
                </div>

                {/* 3. CLINICAL DOMAINS DYNAMIC MAPPING */}
                {isCategoriesLoading ? (
                  <div className="md:col-span-3 py-12 flex justify-center items-center bg-white dark:bg-slate-900 rounded-[2.2rem] border border-slate-200 dark:border-slate-800">
                    <PageLoader compact message="Synchronizing Topic Decks from Registry..." />
                  </div>
                ) : dynamicCategories.length === 0 ? (
                  <div className="md:col-span-3 p-8 bg-slate-50 dark:bg-slate-900 rounded-[2.2rem] border-2 border-dashed border-slate-200 dark:border-slate-800 text-center space-y-3">
                    <BookOpen size={32} className="mx-auto text-slate-400" />
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300">No specific topic question decks found for this state or certification.</p>
                    <p className="text-[10px] text-slate-400">Try launching the <strong>Mixed Practice Marathon</strong> above to access the full randomized question pool.</p>
                  </div>
                ) : (
                  dynamicCategories.map(({ category: d, count }) => {
                    const meta = getDomainMeta(d);
                    const DomainIcon = meta.icon;
                    return (
                        <div 
                            key={d}
                            onClick={() => {
                                setTopic(d);
                                startQuiz(d);
                            }}
                            className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-805 rounded-[2.2rem] p-6 shadow-md hover:shadow-xl transition-all hover:border-indigo-500/30 cursor-pointer transform hover:-translate-y-1 group flex flex-col justify-between h-64 overflow-hidden"
                        >
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className={`px-2.5 py-0.5 rounded-full font-black text-[8px] tracking-widest uppercase ${meta.bg} ${meta.color}`}>
                                        {meta.tag}
                                    </span>
                                    <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 px-2.5 py-0.5 rounded-full border border-indigo-100 dark:border-indigo-900/40 flex items-center gap-1">
                                        <BookOpenCheck size={11}/> {count} {count === 1 ? 'Question' : 'Questions'}
                                    </span>
                                </div>
                                <h4 className="text-lg font-black text-slate-950 dark:text-slate-50 tracking-tight leading-snug group-hover:text-indigo-500 transition-colors line-clamp-1">
                                    {d}
                                </h4>
                                <div className="pt-0.5">
                                    <DifficultyRatingBadge itemId={`quiz-${d}`} isExam={false} compact={true} />
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-bold leading-normal pt-0.5 line-clamp-2">
                                    {meta.desc}
                                </p>
                            </div>

                            <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-850/40 flex justify-between items-center text-[8.5px] font-black text-slate-400 group-hover:text-indigo-500 transition-colors">
                                <div className="flex items-center gap-1">
                                    <DomainIcon size={12} className={meta.color} />
                                    <span>Domain drill</span>
                                </div>
                                <span className="flex items-center gap-0.5 group-hover:translate-x-1 transition-transform">
                                    Start Deck ({count}) <ChevronRight size={10} />
                                </span>
                            </div>
                        </div>
                    );
                })
                )}
            </div>
        </div>

        {/* MISTAKE VAULT BREAKDOWN DETAILS */}
        {isMissedMode && missedCount > 0 && (
            <div className="p-6 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-805 space-y-4 max-w-4xl">
                <h3 className="text-xs font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5"><TrendingDown size={14} className="text-rose-500" /> Vulnerability breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {missedBreakdown.slice(0, 4).map(([cat, count]) => (
                        <div key={cat} className="p-4 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-1.5">
                            <div className="flex justify-between items-center text-[9px] font-black uppercase">
                                <span className="text-slate-800 dark:text-slate-200 truncate">{cat}</span>
                                <span className="text-rose-500">{count} Gaps</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden">
                                <div className="h-full bg-rose-500" style={{ width: `${(count / missedCount) * 100}%` }}></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
      </div>
    );
  }

  if (questions.length === 0) return null;

  const currentQ = questions[currentIndex];
  const isOrderingFlag = currentQ?.type === 'ordering';

  const totalQuestionsCount = Math.max(1, questions.length);
  const remainingQuestionsCount = Math.max(0, questions.length - (currentIndex + 1));
  const questionsProgressPercent = Math.min(100, Math.round(((currentIndex + 1) / totalQuestionsCount) * 100));

  return (
    <div className="max-w-4xl mx-auto pb-12 md:pb-32 animate-fade-in text-slate-905 dark:text-slate-100 min-h-0 pt-3 md:pt-6">
      
      {/* Top Session Progress Bar */}
      <div className="w-full">
        <AdUnit placement={topic === 'Missed Questions' ? 'missed_bank' : 'practice_quiz_top'} refreshKey={adRefreshKey} />
      </div>

      {/* Guest Trial Free Limit Banner */}
      {isGuest && config.isGuestTrialEnabled !== false && (
        <div className="bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/30 rounded-2xl p-3 px-4 flex items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-500 shrink-0" />
            <span className="font-bold text-amber-900 dark:text-amber-200">
              Free Exploration Mode: <strong className="font-black">{Math.min(guestState.totalItemsExplored, guestLimit)} / {guestLimit}</strong> items explored
            </span>
          </div>
          <button
            onClick={() => setShowGuestLimitModal(true)}
            className="text-[11px] font-black uppercase tracking-wider text-amber-700 dark:text-amber-300 hover:underline shrink-0"
          >
            Create Free Account
          </button>
        </div>
      )}

      <div className="flex flex-col space-y-4">
        
        {/* Dynamic header navigation with Circular Progress Rings */}
        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-1.5 sm:p-2 md:p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-lg flex flex-wrap sm:flex-nowrap justify-between items-center sticky top-2 z-50 mb-2 transition-all gap-1.5 sm:gap-2">
          <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-1 min-w-0 flex-wrap sm:flex-nowrap">
            <button 
                onClick={handleBackToRegistry} 
                className="p-2 sm:p-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl transition-all active:scale-90 shrink-0 min-h-[36px] min-w-[36px] sm:min-h-[40px] sm:min-w-[40px] flex items-center justify-center"
                title="Exit Quiz"
            >
                <X size={16} strokeWidth={3}/>
            </button>

            {/* CIRCULAR PROGRESS RING: QUESTIONS REMAINING */}
            <QuizCircularProgressRing 
              progress={questionsProgressPercent}
              size={34}
              strokeWidth={3.5}
              accentColorClass={tokens.accentText}
              label={`${remainingQuestionsCount}`}
              sublabel={
                <>
                  <span className="hidden md:inline">Remaining Questions ({currentIndex + 1}/{totalQuestionsCount})</span>
                  <span className="hidden sm:inline md:hidden">Remaining ({currentIndex + 1}/{totalQuestionsCount})</span>
                  <span className="sm:hidden">Remaining</span>
                </>
              }
            />

            {/* CIRCULAR PROGRESS RING: ELAPSED TIME */}
            <QuizCircularProgressRing 
              progress={questionsProgressPercent}
              size={34}
              strokeWidth={3.5}
              accentColorClass={tokens.accentText}
              label={`${Math.floor(elapsedSeconds / 60)}:${(elapsedSeconds % 60).toString().padStart(2, '0')}`}
              sublabel={
                <>
                  <span className="hidden sm:inline">Time Elapsed</span>
                  <span className="sm:hidden">Elapsed</span>
                </>
              }
            />
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <span className="hidden sm:inline-block px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-[9px] font-black tracking-widest uppercase border border-slate-200 dark:border-slate-700">
              {topic}
            </span>
            <button 
                onClick={handleBackToRegistry} 
                className="hidden sm:flex px-3 py-1.5 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 rounded-xl transition-all active:scale-95 text-[9px] font-black tracking-widest items-center gap-1 border border-rose-100 dark:border-rose-900/30"
            >
                <X size={12}/> Exit
            </button>
          </div>
        </div>

        {/* ACTIVE DRILL WORKSPACE CARD */}
        <div 
          key={currentIndex} 
          className={`rounded-[2.5rem] p-4 sm:p-6 md:p-12 shadow-2xl border flex flex-col min-h-[460px] animate-slide-in-right relative overflow-hidden ${
            config?.enableVibrantCards !== false
              ? (topic === 'Missed Questions'
                ? 'bg-gradient-to-br from-rose-50/70 via-rose-50/30 to-rose-100/10 border-rose-200/80 dark:from-slate-900/90 dark:via-rose-950/35 dark:to-rose-950/55 dark:border-rose-800/60 shadow-[0_4px_20px_-4px_rgba(244,63,94,0.08)]'
                : 'bg-gradient-to-br from-indigo-50/70 via-indigo-50/30 to-indigo-100/10 border-indigo-200/80 dark:from-slate-900/90 dark:via-indigo-950/35 dark:to-indigo-950/55 dark:border-indigo-800/60 shadow-[0_4px_20px_-4px_rgba(99,102,241,0.08)]')
              : 'bg-white dark:bg-slate-900 border-slate-155 dark:border-slate-805'
          }`}
        >
          
          {topic === 'Missed Questions' && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-rose-500/10">
                  <div className="h-full bg-rose-500 transition-all duration-700" style={{ width: `${((MissedQuestionsService.getStreak(currentQ?._id || '')) / 2) * 100}%` }}></div>
              </div>
          )}

          <div className="flex items-center justify-between mb-5">
              <span className="px-3 py-1 text-[8px] md:text-[9px] font-black rounded-lg border tracking-widest uppercase bg-slate-50 dark:bg-slate-950 text-indigo-600 dark:text-indigo-400 border-slate-100 dark:border-slate-800 flex items-center gap-1">
                {isOrderingFlag ? <><ListOrdered size={12}/> Sequencing drill</> : <><Target size={12} /> {currentQ?.category}</>}
              </span>
              
              {isOrderingFlag && !isAnswered && currentSequence.length > 0 && (
                  <button onClick={() => setCurrentSequence([])} className="p-2 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-rose-500 rounded-lg transition-all active:scale-90" title="Reset"><RotateCcw size={14}/></button>
              )}
          </div>
          
          <div className="mb-6 md:mb-8 border-b border-slate-100 dark:border-slate-800/60 pb-5 md:pb-6 flex items-start justify-between gap-4">
                <h3 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white leading-snug flex-1">
                    "{currentQ?.text}"
                </h3>
                <button 
                  onClick={() => {
                    if (currentQ) {
                      const isOrdering = currentQ.type === 'ordering';
                      const optionsText = currentQ.options 
                        ? currentQ.options.map((o, idx) => 
                            isOrdering ? `Step ${idx + 1}: ${o.text}` : `Choice ${String.fromCharCode(65 + idx)}: ${o.text}`
                          ).join('. ') 
                        : '';
                      const rationaleSpoken = (isAnswered && currentQ.rationale) ? `. Explanation rationale: ${currentQ.rationale}` : '';
                      handleSpeak(`${currentQ.text}. ${optionsText}${rationaleSpoken}`);
                    }
                  }} 
                  className={`p-3.5 sm:p-3 rounded-xl transition-all shrink-0 active:scale-95 border min-h-[44px] min-w-[44px] flex items-center justify-center ${
                    isSpeaking 
                      ? 'bg-rose-50 text-rose-500 animate-pulse border-rose-200 dark:bg-rose-950/25 dark:border-rose-900/35' 
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-indigo-400 border-transparent'
                  }`}
                  title={isSpeaking ? "Stop Speaking" : "Auditory Playback (TTS)"}
                  aria-label={isSpeaking ? "Stop reading question" : "Read question and choices aloud"}
                >
                  {isSpeaking ? <VolumeX size={20} className="text-rose-500" /> : <Volume2 size={20} />}
                </button>
          </div>
          
          {/* Answer choices Grid layouts */}
          <div className="space-y-3">
              {(currentQ?.options || []).map((opt, idx) => {
                  const val = opt._id || idx.toString();
                  const seqIdx = isOrderingFlag ? currentSequence.indexOf(val) : -1;
                  const isSelected = isOrderingFlag ? seqIdx !== -1 : selectedOptionId === val;
                  
                  let style = `border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/90 hover:bg-indigo-50 dark:hover:bg-indigo-950/80 hover:border-indigo-600 dark:hover:border-indigo-400 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] text-slate-900 dark:text-slate-100 font-bold`;
                  
                  if (isAnswered) {
                    if (isOrderingFlag) {
                        const correctSequence = currentQ!.options.map((o, oIdx) => o._id || oIdx.toString());
                        const isAtCorrectPos = correctSequence[seqIdx] === val;
                        
                        if (isAtCorrectPos) style = "border-emerald-500 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200 scale-[1.01]";
                        else if (isSelected) style = "border-rose-500 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-955/40 text-rose-900 dark:text-rose-200";
                        else style = "opacity-30 border-transparent bg-slate-50 dark:bg-slate-950";
                    } else {
                        if (opt.isCorrect) style = "border-emerald-500 dark:border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-100 ring-4 ring-emerald-550/20 scale-[1.01]";
                        else if (isSelected) style = "border-rose-500 dark:border-rose-500 bg-rose-50 dark:bg-rose-955/40 text-rose-900 dark:text-rose-200 opacity-80 z-10";
                        else style = "opacity-25 border-transparent bg-slate-50 dark:bg-slate-950 scale-[0.98]";
                    }
                  } else if (isSelected) {
                    style = `${tokens.accentBorder} ${tokens.badgeBg} ${tokens.accentText} scale-[1.01]`;
                  }

                  return (
                    <button 
                        key={val} 
                        onClick={() => handleOptionSelect(val, !!opt.isCorrect)} 
                        disabled={isAnswered || (isOrderingFlag && isSelected)}
                        className={`w-full p-4 md:p-5 rounded-2xl border-2 text-left font-bold flex items-center gap-4 transition-all duration-300 relative group min-h-[64px] ${style}`}
                    >
                        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl border-2 flex items-center justify-center shrink-0 font-black text-xs transition-all duration-300 ${isSelected ? `${tokens.primaryBtn} text-white shadow-md` : `bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 group-hover:border-indigo-600 dark:group-hover:border-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900 group-hover:scale-105`}`}>
                            {isOrderingFlag && isSelected ? (seqIdx + 1) : String.fromCharCode(65 + idx)}
                        </div>
                        <span className="text-sm md:text-base leading-snug flex-1 pr-3">{opt.text}</span>
                        
                        {isAnswered && !isOrderingFlag && opt.isCorrect && (
                            <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shrink-0"><Check size={12} className="text-white font-black" /></div>
                        )}
                    </button>
                  );
              })}
          </div>

          {/* INSTANT CLINICAL FEEDBACK & GEMINI AI DEEP RATIONALE */}
          {isAnswered && (
            <div className="mt-8 space-y-4 animate-slide-up">
              
              {/* Correct / Incorrect Header Callout Box */}
              <div className={`p-5 md:p-8 rounded-[2rem] border relative overflow-hidden ${isCorrect ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/30' : 'bg-rose-50 dark:bg-rose-955/20 border-rose-100 dark:border-rose-900/30'}`}>
                <div className="absolute top-0 right-0 p-8 opacity-5"><Lightbulb size={60} /></div>
                <div className="relative z-10 space-y-2">
                    <div className="flex items-center gap-2">
                        {isCorrect ? (
                            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white"><Check size={10} className="font-extrabold" /></div>
                        ) : (
                            <div className="w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center text-white font-black">X</div>
                        )}
                        <h4 className={`text-[10px] font-black uppercase tracking-widest ${isCorrect ? 'text-emerald-600 dark:text-emerald-450' : 'text-rose-600 dark:text-rose-450'}`}>
                            {isCorrect ? 'Clinical Validation Correct' : 'Logic Discrepancy Found'}
                        </h4>
                    </div>
                    <div className="border-l-4 pl-3.5 md:pl-4 mt-2 py-0.5 leading-relaxed" style={{borderColor: isCorrect ? 'rgb(16 185 129)' : 'rgb(244 63 94)'}}>
                        <p className={`text-sm md:text-base font-bold ${isCorrect ? 'text-slate-900 dark:text-slate-100' : 'text-slate-900 dark:text-slate-105'}`}>
                            "{currentQ?.explanation}"
                        </p>
                    </div>
                </div>
              </div>

              {/* Loader during Clinical Faculty Deep Audit call */}
              {isAiLoading && (
                <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl">
                  <PageLoader compact message="Retrieving faculty clinical breakdown..." size="sm" />
                </div>
              )}

              {/* Analyze clinical logic option - Faculty Clinical Review */}
              {!isCorrect && !aiRationale && !isAiLoading && config.enableAIRationales && currentQ?.type !== 'ordering' && (
                <button 
                    id="explain-my-mismatch-btn"
                    onClick={handleExplainMismatchClick}
                    className={`w-full flex items-center justify-between p-5 md:p-6 ${tokens.heroBannerBg} text-white rounded-3xl border-b-4 border-slate-700 active:border-b-0 active:translate-y-1 transition-all group shadow-xl cursor-pointer`}
                >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl ${tokens.primaryBtn} flex items-center justify-center shadow-lg group-hover:rotate-12 transition-transform`}>
                        <GraduationCap size={20} className="text-white" />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-xs md:text-sm font-black tracking-tight flex items-center gap-1.5 text-white">
                              Explain My Mismatch
                          </h4>
                          {isGuest && (
                            <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-amber-400/20 text-amber-300 border border-amber-400/30 flex items-center gap-1">
                              <Lock size={9} /> Free Member Unlock
                            </span>
                          )}
                        </div>
                        <p className="text-[8.5px] font-bold text-slate-400 tracking-widest uppercase leading-none mt-1">
                          {isGuest ? 'Create free account to view verified faculty rationale for your exact selection' : 'Faculty Clinical Breakdown: Why was my exact selection wrong?'}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors ${
                      isGuest 
                        ? 'bg-amber-400/15 border-amber-400/30 text-amber-300 group-hover:bg-amber-400/25' 
                        : 'bg-white/10 border-white/10 group-hover:bg-indigo-500/20 text-white'
                    }`}>
                      {isGuest ? (
                        <>
                          <Lock size={11} className="text-amber-400" />
                          <span className="text-[8px] font-black tracking-widest uppercase">Unlock Review</span>
                        </>
                      ) : (
                        <>
                          <GraduationCap size={13} className={`${tokens.accentText}`} />
                          <span className="text-[8px] font-black tracking-widest uppercase">Faculty Audit</span>
                        </>
                      )}
                    </div>
                </button>
              )}

              {/* Expanded Faculty clinical rationale response panel */}
              {!isCorrect && aiRationale && (
                <div className={`p-6 md:p-8 ${tokens.heroBannerBg} text-white rounded-[2rem] border border-white/10 shadow-2xl relative overflow-hidden group`}>
                  <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                    <Sparkles size={80} />
                  </div>
                  <div className="relative z-10 space-y-5">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg ${tokens.primaryBtn} flex items-center justify-center shadow-lg`}>
                        <GraduationCap size={16} className="text-white" />
                      </div>
                      <div>
                        <h4 className={`text-xs font-black tracking-tight ${tokens.accentText}`}>Faculty Clinical Safety Analysis</h4>
                        <p className="text-[8px] font-black text-slate-400 tracking-widest uppercase">Verified Nurse Educator Breakdown</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl">
                        <p className="text-[8px] font-black text-rose-400 tracking-wider mb-1 uppercase">Reason for option error (Faculty Rationale):</p>
                        <p className="text-xs md:text-sm font-bold opacity-90 leading-relaxed text-slate-200">
                          {aiRationale.whyIncorrect}
                        </p>
                      </div>

                      {aiRationale.correctRationale && (
                        <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                          <p className="text-[8px] font-black text-emerald-400 tracking-wider mb-1 uppercase">Clinical gold standard (Faculty Guidance):</p>
                          <p className="text-xs md:text-sm font-bold opacity-90 leading-relaxed text-slate-200">
                            {aiRationale.correctRationale}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 pt-3 border-t border-white/5 opacity-80 text-[8.5px] font-bold tracking-wider text-slate-300">
                      <ShieldCheck size={13} className="text-emerald-400 shrink-0" />
                      <span>Reviewed & verified by Diamond Clinical Nursing Faculty and State Board Standards</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Inline Quiz Question Ad Slot */}
              <div className="mt-4">
                <AdUnit placement="inline_quiz" refreshKey={adRefreshKey} />
              </div>
            </div>
          )}
        </div>

        {/* Keyboard layout visual indicators */}
        <div className="flex justify-center items-center gap-4 text-[9px] font-black text-slate-450 dark:text-slate-500 tracking-widest uppercase px-1">
            <span className="flex items-center gap-1"><Keyboard size={12}/> Shortcuts:</span>
            {!isAnswered ? (
                <>
                    <span>[A-D] / [1-4] Answer Select</span>
                </>
            ) : (
                <>
                    <span>[Space] / [ArrowRight] Next Item</span>
                </>
            )}
        </div>
      </div>

      <div className="w-full mt-4">
        <AdUnit placement="practice_quiz_bottom" refreshKey={adRefreshKey} />
      </div>

      {/* Guest Exploration Limit Conversion Modal */}
      <GuestLimitModal 
        isOpen={showGuestLimitModal}
        onClose={() => setShowGuestLimitModal(false)}
        limit={guestLimit}
        totalExplored={guestState.totalItemsExplored}
        featureName="Practice Quiz"
      />

      {/* Faculty Clinical Feature Sign-Up / Login Conversion Modal */}
      <AiFeatureAuthModal 
        isOpen={showAiAuthModal}
        onClose={() => setShowAiAuthModal(false)}
        featureTitle="Faculty Clinical Mismatch Breakdown"
        featureDescription="Get personalized faculty clinical audits explaining why your selected answer was wrong and how nurse educators and board examiners evaluate patient safety."
        source="practice_quiz_explain_mismatch"
      />

      {/* STICKY INTERACTIVE FOOTER TRANSITION TO NEXT ITEM */}
      {isAnswered && (
        <div className="sticky bottom-3 z-[100] pb-safe animate-pop mt-6 px-1 md:px-0">
            <div className="max-w-2xl mx-auto">
                <button 
                    onClick={handleNext} 
                    className={`w-full py-5 text-white rounded-2xl font-black text-xs tracking-widest shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-all active:scale-95 flex items-center justify-center gap-3 border-b-4 border-black/20 ${topic === 'Missed Questions' ? 'bg-rose-600 hover:bg-rose-700' : tokens.primaryBtn}`}
                >
                    {currentIndex === questions.length - 1 ? 'Finish Remediation Drill' : 'Continue to Next Item'} <ArrowRight size={18} />
                </button>
            </div>
        </div>
      )}
    </div>
  );
};
